import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, ArrowLeft, Check, ChevronRight, Clock, MapPin, CalendarDays, Info, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { format, parseISO, isSameDay } from "date-fns";

const BookingPage = () => {
  const { photographerId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photographer, setPhotographer] = useState(null);
  const [photographerError, setPhotographerError] = useState(null);
  const [photographerRetryAttempted, setPhotographerRetryAttempted] = useState(false);
  const [availabilityData, setAvailabilityData] = useState({
    availability_windows: [],
    confirmed_bookings: [],
    blocked_times: [],
    photographer_timezone: "America/New_York"
  });
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bookingMode, setBookingMode] = useState("available"); // "available" or "request"
  const [calendarMonth, setCalendarMonth] = useState(new Date()); // Track displayed month
  const [nextAvailableSuggestion, setNextAvailableSuggestion] = useState(null);
  
  const [formData, setFormData] = useState({
    event_type: "",
    request_types: [],
    event_date: null,
    start_time: "",
    end_time: "",
    is_tournament: false,
    tournament_start_date: null,
    tournament_end_date: null,
    venue_name: "",
    venue_address: "",
    team_name: "",
    jersey_numbers: "",
    notes: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_instagram: ""
  });

  useEffect(() => {
    fetchPhotographer();
    fetchAvailabilityData();
  }, [photographerId]);

  const fetchPhotographer = async () => {
    setPhotographerError(null);
    
    const run = async () => {
      const response = await axios.get(`${API}/photographer/${photographerId}/public`);
      return response;
    };
    
    try {
      const response = await run();
      setPhotographer(response.data);
      setPhotographerRetryAttempted(false);
    } catch (err) {
      const status = err?.response?.status;
      
      // If not found, show error UI
      if (status === 404) {
        console.warn("NAVIGATING TO ERROR STATE DUE TO 404", {
          path: window.location.pathname,
          errorStatus: status
        });
        setPhotographerError({ type: "not_found", message: "Photographer not found." });
        return;
      }
      
      // Transient errors - retry once
      const transientStatuses = [520, 502, 503, 504];
      const isNetworkError = !err?.response;
      
      if (!photographerRetryAttempted && (transientStatuses.includes(status) || isNetworkError)) {
        console.warn("Transient error, retrying...", { status, isNetworkError });
        setPhotographerRetryAttempted(true);
        await new Promise(r => setTimeout(r, 600));
        try {
          const response = await run();
          setPhotographer(response.data);
          setPhotographerError(null);
          return;
        } catch (err2) {
          // fall through to error state
        }
      }
      
      console.warn("SHOWING ERROR STATE DUE TO API ERROR", {
        path: window.location.pathname,
        errorStatus: status,
        isNetworkError
      });
      setPhotographerError({ type: "temporary", message: "We're having trouble loading this photographer. Please try again." });
    }
  };

  const fetchAvailabilityData = async (dateFrom = null, dateTo = null) => {
    try {
      let url = `${API}/availability/slots/public/${photographerId}`;
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setAvailabilityData(response.data);
    } catch (error) {
      console.error("Failed to fetch availability:", error);
    }
  };

  // Helper: Convert HH:MM to minutes since midnight
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper: Convert minutes to HH:MM
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Build busy intervals (booking + buffer) for a specific date
  const getBusyIntervalsForDate = (date) => {
    const busyIntervals = [];
    
    // Get availability windows for this date (to know buffer settings)
    const windowsForDate = availabilityData.availability_windows.filter(w => w.date === date);
    
    // Default buffer (0 if no windows found)
    let bufferBefore = 0;
    let bufferAfter = 0;
    if (windowsForDate.length > 0) {
      bufferBefore = windowsForDate[0].buffer_before || 0;
      bufferAfter = windowsForDate[0].buffer_after || 0;
    }
    
    // Add confirmed bookings with buffer
    availabilityData.confirmed_bookings
      .filter(b => b.event_date === date && b.start_time && b.end_time)
      .forEach(booking => {
        const expandedStart = timeToMinutes(booking.start_time) - bufferBefore;
        const expandedEnd = timeToMinutes(booking.end_time) + bufferAfter;
        busyIntervals.push({
          start: expandedStart,
          end: expandedEnd,
          actualStart: timeToMinutes(booking.start_time),
          actualEnd: timeToMinutes(booking.end_time),
          type: 'booking'
        });
      });
    
    // Add blocked times
    availabilityData.blocked_times
      .filter(b => b.date === date)
      .forEach(blocked => {
        busyIntervals.push({
          start: timeToMinutes(blocked.start_time || '00:00'),
          end: timeToMinutes(blocked.end_time || '23:59'),
          type: 'blocked'
        });
      });
    
    return busyIntervals.sort((a, b) => a.start - b.start);
  };

  // Check if a slot overlaps any busy interval
  const isSlotUnavailable = (slotStart, slotEnd, busyIntervals) => {
    const startMins = timeToMinutes(slotStart);
    const endMins = timeToMinutes(slotEnd);
    
    for (const interval of busyIntervals) {
      // Half-open interval overlap: slotStart < busyEnd && slotEnd > busyStart
      if (startMins < interval.end && endMins > interval.start) {
        // Determine reason: is it the actual booking or the buffer?
        if (interval.type === 'blocked') {
          return { unavailable: true, reason: 'blocked' };
        }
        if (startMins < interval.actualEnd && endMins > interval.actualStart) {
          return { unavailable: true, reason: 'booked' };
        }
        return { unavailable: true, reason: 'buffer' };
      }
    }
    return { unavailable: false, reason: null };
  };

  // Find next available slot on a date
  const findNextAvailableSlot = (date, afterTime, busyIntervals) => {
    const windowsForDate = availabilityData.availability_windows.filter(w => w.date === date);
    if (windowsForDate.length === 0) return null;
    
    for (const window of windowsForDate) {
      const windowStart = timeToMinutes(window.start_time);
      const windowEnd = timeToMinutes(window.end_time);
      let searchStart = Math.max(timeToMinutes(afterTime), windowStart);
      
      // Search in 30-minute increments
      let current = searchStart;
      while (current + 30 <= windowEnd) {
        const slotStart = minutesToTime(current);
        const slotEnd = minutesToTime(current + 30);
        
        const { unavailable } = isSlotUnavailable(slotStart, slotEnd, busyIntervals);
        if (!unavailable) {
          return { start_time: slotStart, end_time: slotEnd };
        }
        current += 30;
      }
    }
    
    return null;
  };

  // Generate time slots with availability status
  const generateTimeSlotsWithStatus = useCallback((windows) => {
    const slots = [];
    const date = windows[0]?.date;
    if (!date) return slots;
    
    const busyIntervals = getBusyIntervalsForDate(date);
    
    windows.forEach(window => {
      const start = timeToMinutes(window.start_time);
      const end = timeToMinutes(window.end_time);
      
      // Generate 30-minute slots
      for (let time = start; time + 30 <= end; time += 30) {
        const slotStart = minutesToTime(time);
        const slotEnd = minutesToTime(time + 30);
        const { unavailable, reason } = isSlotUnavailable(slotStart, slotEnd, busyIntervals);
        
        slots.push({
          start_time: slotStart,
          end_time: slotEnd,
          date: date,
          available: !unavailable,
          unavailableReason: reason
        });
      }
    });
    
    return slots.sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [availabilityData]);

  // Group windows by date and generate slots
  const slotsByDate = useMemo(() => {
    const grouped = {};
    availabilityData.availability_windows.forEach(window => {
      if (!grouped[window.date]) {
        grouped[window.date] = [];
      }
      grouped[window.date].push(window);
    });
    
    // Generate time slots for each date
    const result = {};
    Object.entries(grouped).forEach(([date, windows]) => {
      result[date] = generateTimeSlotsWithStatus(windows);
    });
    
    return result;
  }, [availabilityData, generateTimeSlotsWithStatus]);

  // Get dates with available slots for calendar highlighting
  const datesWithSlots = useMemo(() => {
    return Object.keys(slotsByDate).map(d => new Date(d + "T00:00:00"));
  }, [slotsByDate]);

  // Get next 5 upcoming dates with availability
  const upcomingDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return Object.keys(slotsByDate)
      .map(dateStr => {
        const date = new Date(dateStr + "T00:00:00");
        const slots = slotsByDate[dateStr];
        // Get the time range for this date
        const startTime = slots[0]?.start_time;
        const endTime = slots[slots.length - 1]?.end_time;
        const location = slots[0]?.location;
        
        return {
          dateStr,
          date,
          startTime,
          endTime,
          location,
          slotCount: slots.length
        };
      })
      .filter(item => item.date >= today)
      .sort((a, b) => a.date - b.date)
      .slice(0, 5);
  }, [slotsByDate]);

  // Generate 30-min time slots from available slots
  const generateTimeSlots = (slot) => {
    const slots = [];
    const [startHour, startMin] = slot.start_time.split(":").map(Number);
    const [endHour, endMin] = slot.end_time.split(":").map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = `${String(currentHour).padStart(2, "0")}:${String(currentMin).padStart(2, "0")}`;
      const nextMin = currentMin + 30;
      const nextHour = nextMin >= 60 ? currentHour + 1 : currentHour;
      const nextMinNorm = nextMin >= 60 ? nextMin - 60 : nextMin;
      const endTimeStr = `${String(nextHour).padStart(2, "0")}:${String(nextMinNorm).padStart(2, "0")}`;
      
      slots.push({
        slot_id: slot.slot_id,
        date: slot.date,
        start_time: timeStr,
        end_time: endTimeStr,
        location: slot.location,
        location_address: slot.location_address,
        parent_slot: slot
      });
      
      currentMin = nextMinNorm;
      currentHour = nextHour;
    }
    
    return slots;
  };

  // Get time slots for selected date (already generated with availability status)
  const timeSlotsForDate = useMemo(() => {
    if (!formData.event_date) return [];
    const dateStr = format(formData.event_date, "yyyy-MM-dd");
    return slotsByDate[dateStr] || [];
  }, [formData.event_date, slotsByDate]);

  // Handle time slot click - show message for unavailable, toggle for available
  const handleSlotClick = (timeSlot) => {
    if (!timeSlot.available) {
      // Show toast for unavailable slot
      toast.error("That time is unavailable.");
      
      // Find next available slot
      const dateStr = format(formData.event_date, "yyyy-MM-dd");
      const busyIntervals = getBusyIntervalsForDate(dateStr);
      const nextSlot = findNextAvailableSlot(dateStr, timeSlot.end_time, busyIntervals);
      
      if (nextSlot) {
        const formattedTime = new Date(`2000-01-01T${nextSlot.start_time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        setNextAvailableSuggestion({
          date: dateStr,
          time: formattedTime,
          slot: nextSlot
        });
      } else {
        setNextAvailableSuggestion({ noAvailability: true });
      }
      return;
    }
    
    // Clear next available suggestion when selecting an available slot
    setNextAvailableSuggestion(null);
    
    // Toggle selection for available slots
    handleSlotToggle(timeSlot);
  };

  // Handle time slot selection (allow multiple sequential)
  const handleSlotToggle = (timeSlot) => {
    setSelectedSlots(prev => {
      const isSelected = prev.some(s => 
        s.date === timeSlot.date && s.start_time === timeSlot.start_time
      );
      
      if (isSelected) {
        // Remove this slot
        return prev.filter(s => 
          !(s.date === timeSlot.date && s.start_time === timeSlot.start_time)
        );
      } else {
        // Add slot - check if it's sequential with existing
        const newSlots = [...prev, timeSlot];
        
        // Sort by start time
        newSlots.sort((a, b) => a.start_time.localeCompare(b.start_time));
        
        // Verify all slots are sequential (no gaps)
        for (let i = 1; i < newSlots.length; i++) {
          if (newSlots[i].start_time !== newSlots[i-1].end_time) {
            toast.error("Please select consecutive time slots only");
            return prev;
          }
        }
        
        return newSlots;
      }
    });
  };

  // Check if a slot is selected
  const isSlotSelected = (timeSlot) => {
    return selectedSlots.some(s => 
      s.date === timeSlot.date && s.start_time === timeSlot.start_time
    );
  };

  // Update form data when slots change
  useEffect(() => {
    if (selectedSlots.length > 0) {
      const firstSlot = selectedSlots[0];
      const lastSlot = selectedSlots[selectedSlots.length - 1];
      setFormData(prev => ({
        ...prev,
        start_time: firstSlot.start_time,
        end_time: lastSlot.end_time,
        venue_name: firstSlot.location || prev.venue_name,
        venue_address: firstSlot.location_address || prev.venue_address
      }));
    }
  }, [selectedSlots]);

  const handleRequestTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      request_types: prev.request_types.includes(type)
        ? prev.request_types.filter(t => t !== type)
        : [...prev.request_types, type]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        photographer_id: photographerId,
        event_type: formData.event_type,
        request_types: formData.request_types,
        event_date: formData.event_date ? format(formData.event_date, "yyyy-MM-dd") : null,
        start_time: formData.start_time,
        end_time: formData.end_time,
        is_tournament: formData.is_tournament,
        tournament_start_date: formData.tournament_start_date ? format(formData.tournament_start_date, "yyyy-MM-dd") : null,
        tournament_end_date: formData.tournament_end_date ? format(formData.tournament_end_date, "yyyy-MM-dd") : null,
        venue_name: formData.venue_name,
        venue_address: formData.venue_address,
        team_name: formData.team_name,
        jersey_numbers: formData.jersey_numbers,
        notes: formData.notes,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_instagram: formData.customer_instagram,
        availability_slot_ids: selectedSlots.map(s => s.slot_id).filter((v, i, a) => a.indexOf(v) === i)
      };

      const response = await axios.post(`${API}/bookings/request`, payload);
      
      toast.success("Booking request submitted!");
      navigate(`/portal/${response.data.magic_link_token}`);
    } catch (error) {
      // Handle 409 Conflict - time slot no longer available
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        toast.error("That time was just booked. Please choose another slot.");
        
        // Auto-refresh availability data
        await fetchAvailabilityData();
        
        // Clear selection
        setSelectedSlots([]);
        setFormData(prev => ({
          ...prev,
          start_time: "",
          end_time: ""
        }));
        
        // Show next available if provided
        if (detail?.next_available) {
          const nextTime = new Date(`2000-01-01T${detail.next_available.start_time}`).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          setNextAvailableSuggestion({
            date: detail.next_available.date,
            time: nextTime,
            slot: detail.next_available
          });
        }
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to submit booking request");
      }
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = formData.event_type && formData.request_types.length > 0;
  const canProceedStep2 = formData.is_tournament 
    ? (formData.tournament_start_date && formData.tournament_end_date)
    : (bookingMode === "available" ? selectedSlots.length > 0 : formData.event_date);
  const canSubmit = formData.customer_name && formData.customer_email && formData.customer_phone;

  const eventTypes = photographer?.event_types || ["Sports", "Photoshoot", "Wedding"];
  const requestTypes = photographer?.request_types || ["Photos", "Video", "Drone"];

  // Get branding
  const brandName = photographer?.company_name || "360Lumina";
  const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;

  // Error states - show inline UI instead of redirecting
  if (photographerError?.type === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="max-w-md text-center">
          <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Photographer not found</h1>
          <p className="text-muted-foreground mb-6">
            This photographer profile may have been moved or is unavailable.
          </p>
          <Button
            onClick={() => window.location.href = "/"}
            className="btn-primary"
          >
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  if (photographerError?.type === "temporary") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="max-w-md text-center">
          <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Could not load this page</h1>
          <p className="text-muted-foreground mb-6">{photographerError.message}</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setPhotographerRetryAttempted(false);
                fetchPhotographer();
              }}
              className="btn-primary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = "/"}
            >
              Back to home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        
        <div className="flex items-center gap-3 mb-2">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-8 max-w-[140px] object-contain" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-primary" />
              <span className="text-2xl font-serif">{brandName}</span>
            </>
          )}
        </div>
        
        <h1 className="text-4xl md:text-5xl tracking-tight mb-4">Book a Session</h1>
        <p className="text-muted-foreground">
          {photographer?.user?.name && `with ${photographer.user.name}`}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="max-w-2xl mx-auto mb-12">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                  step >= s
                    ? "bg-primary border-primary text-white"
                    : "border-white/20 text-muted-foreground"
                }`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`w-24 md:w-32 h-0.5 mx-2 transition-colors ${
                    step > s ? "bg-primary" : "bg-white/20"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Service Type</span>
          <span>Date & Time</span>
          <span>Your Details</span>
        </div>
      </div>

      {/* Form Steps */}
      <div className="max-w-2xl mx-auto">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-card border border-border rounded-sm p-8"
        >
          {/* Step 1: Service Type */}
          {step === 1 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl mb-2">What type of event?</h2>
                <p className="text-muted-foreground text-sm">Select one event type</p>
              </div>

              <RadioGroup
                value={formData.event_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
                className="grid gap-4"
              >
                {eventTypes.map((type) => (
                  <div
                    key={type}
                    className={`flex items-center space-x-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                      formData.event_type === type
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-white/40"
                    }`}
                  >
                    <RadioGroupItem value={type} id={type} data-testid={`event-type-${type.toLowerCase()}`} />
                    <Label htmlFor={type} className="cursor-pointer flex-1">{type}</Label>
                  </div>
                ))}
              </RadioGroup>

              <div>
                <h3 className="text-xl mb-4">What services do you need?</h3>
                <p className="text-muted-foreground text-sm mb-4">Select all that apply</p>
                
                <div className="grid gap-4">
                  {requestTypes.map((type) => (
                    <div
                      key={type}
                      className={`flex items-center space-x-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                        formData.request_types.includes(type)
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-white/40"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestTypeToggle(type);
                      }}
                    >
                      <Checkbox
                        checked={formData.request_types.includes(type)}
                        onCheckedChange={(checked) => {
                          // Only toggle if the state would actually change
                          const isCurrentlyChecked = formData.request_types.includes(type);
                          if (checked !== isCurrentlyChecked) {
                            handleRequestTypeToggle(type);
                          }
                        }}
                        data-testid={`request-type-${type.toLowerCase()}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Label className="cursor-pointer flex-1" onClick={(e) => e.stopPropagation()}>{type}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                className="btn-primary rounded-sm w-full"
                disabled={!canProceedStep1}
                onClick={() => setStep(2)}
                data-testid="step1-next"
              >
                Continue <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Step 2: Date & Time with Availability Calendar */}
          {step === 2 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl mb-2">When is your event?</h2>
                <p className="text-muted-foreground text-sm">
                  {availabilityData.availability_windows.length > 0 
                    ? "Select from available time slots or request a custom time"
                    : "Select your preferred date and time"
                  }
                </p>
              </div>

              {formData.event_type === "Sports" && (
                <div className="flex items-center space-x-3 p-4 border border-border rounded-sm">
                  <Checkbox
                    checked={formData.is_tournament}
                    onCheckedChange={(checked) => {
                      setFormData(prev => ({ ...prev, is_tournament: checked }));
                      setSelectedSlots([]);
                      setBookingMode("request");
                    }}
                    data-testid="is-tournament"
                  />
                  <Label>Tournament / Variable Schedule</Label>
                </div>
              )}

              {formData.is_tournament ? (
                /* Tournament Date Range Picker */
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="mb-2 block">Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left" data-testid="tournament-start-date">
                          {formData.tournament_start_date
                            ? format(formData.tournament_start_date, "PPP")
                            : "Select start date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.tournament_start_date}
                          onSelect={(date) => setFormData(prev => ({ ...prev, tournament_start_date: date }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="mb-2 block">End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left" data-testid="tournament-end-date">
                          {formData.tournament_end_date
                            ? format(formData.tournament_end_date, "PPP")
                            : "Select end date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.tournament_end_date}
                          onSelect={(date) => setFormData(prev => ({ ...prev, tournament_end_date: date }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              ) : (
                <>
                  {/* Booking Mode Toggle */}
                  {availabilityData.availability_windows.length > 0 && (
                    <div className="flex gap-2 p-1 bg-muted/30 rounded-sm">
                      <button
                        className={`flex-1 px-4 py-2 rounded-sm text-sm transition-colors ${
                          bookingMode === "available" 
                            ? "bg-primary text-white" 
                            : "text-muted-foreground hover:text-white"
                        }`}
                        onClick={() => {
                          setBookingMode("available");
                          setSelectedSlots([]);
                        }}
                      >
                        <CalendarDays className="w-4 h-4 inline-block mr-2" />
                        Available Slots
                      </button>
                      <button
                        className={`flex-1 px-4 py-2 rounded-sm text-sm transition-colors ${
                          bookingMode === "request" 
                            ? "bg-primary text-white" 
                            : "text-muted-foreground hover:text-white"
                        }`}
                        onClick={() => {
                          setBookingMode("request");
                          setSelectedSlots([]);
                        }}
                      >
                        <Clock className="w-4 h-4 inline-block mr-2" />
                        Request Different Time
                      </button>
                    </div>
                  )}

                  {bookingMode === "available" && availabilityData.availability_windows.length > 0 ? (
                    /* Availability-based Booking */
                    <div className="space-y-6">
                      {/* Calendar and Upcoming Events Side by Side */}
                      <div className="grid md:grid-cols-[1fr,auto] gap-6">
                        {/* Calendar */}
                        <div>
                          <Label className="mb-2 block">Select Date</Label>
                          <Calendar
                            mode="single"
                            selected={formData.event_date}
                            month={calendarMonth}
                            onMonthChange={setCalendarMonth}
                            onSelect={(date) => {
                              setFormData(prev => ({ ...prev, event_date: date }));
                              setSelectedSlots([]);
                            }}
                            modifiers={{ available: datesWithSlots }}
                            modifiersStyles={{
                              available: {
                                backgroundColor: "hsl(var(--primary) / 0.2)",
                                color: "hsl(var(--primary))",
                                fontWeight: "bold"
                              }
                            }}
                            className="rounded-sm border"
                            data-testid="availability-calendar"
                          />
                          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="w-4 h-4 rounded bg-primary/20" />
                            <span>Highlighted dates have available slots</span>
                          </div>
                        </div>

                        {/* Upcoming Events Sidebar */}
                        {upcomingDates.length > 0 && (
                          <div className="md:w-56">
                            <Label className="mb-2 block">Upcoming Availability</Label>
                            <div className="space-y-2">
                              {upcomingDates.map((item) => {
                                const isSelected = formData.event_date && 
                                  format(formData.event_date, "yyyy-MM-dd") === item.dateStr;
                                
                                return (
                                  <button
                                    key={item.dateStr}
                                    className={`w-full p-3 rounded-sm text-left border transition-all ${
                                      isSelected
                                        ? "bg-primary border-primary text-white"
                                        : "border-border hover:border-primary/50 hover:bg-primary/5"
                                    }`}
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, event_date: item.date }));
                                      setSelectedSlots([]);
                                      setCalendarMonth(item.date); // Navigate calendar to this month
                                    }}
                                    data-testid={`upcoming-${item.dateStr}`}
                                  >
                                    <div className="font-medium text-sm">
                                      {format(item.date, "EEE, MMM d")}
                                    </div>
                                    <div className={`text-xs mt-1 flex items-center gap-1 ${
                                      isSelected ? "text-white/80" : "text-muted-foreground"
                                    }`}>
                                      <Clock className="w-3 h-3" />
                                      {item.startTime} - {item.endTime}
                                    </div>
                                    {item.location && (
                                      <div className={`text-xs mt-1 flex items-center gap-1 ${
                                        isSelected ? "text-white/70" : "text-muted-foreground"
                                      }`}>
                                        <MapPin className="w-3 h-3" />
                                        <span className="truncate">{item.location}</span>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Time Slots */}
                      {formData.event_date && (
                        <div>
                          <Label className="mb-2 block">
                            Select Time Slots for {format(formData.event_date, "MMMM d, yyyy")}
                          </Label>
                          
                          {/* Timezone indicator */}
                          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Times shown in {availabilityData.photographer_timezone || "America/New_York"}
                          </p>
                          
                          {timeSlotsForDate.length > 0 ? (
                            <>
                              {/* Legend */}
                              <div className="flex flex-wrap gap-4 mb-3 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-sm border border-border bg-background"></div>
                                  <span>Available</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-sm bg-muted-foreground/30"></div>
                                  <span>Booked</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-sm bg-muted-foreground/15 border border-dashed border-muted-foreground/30"></div>
                                  <span>Buffer</span>
                                </div>
                              </div>
                              
                              <p className="text-sm text-muted-foreground mb-3">
                                Click to select consecutive 30-minute slots
                              </p>
                              
                              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {timeSlotsForDate.map((slot, idx) => {
                                  const isSelected = isSlotSelected(slot);
                                  const isBooked = !slot.available && slot.unavailableReason === 'booked';
                                  const isBuffer = !slot.available && slot.unavailableReason === 'buffer';
                                  const isUnavailable = !slot.available;
                                  
                                  return (
                                    <TooltipProvider key={`${slot.date}-${slot.start_time}`}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            className={`p-3 rounded-sm text-sm border transition-all ${
                                              isSelected
                                                ? "bg-primary border-primary text-white"
                                                : isBooked
                                                  ? "bg-muted-foreground/30 border-transparent text-muted-foreground/60 cursor-not-allowed"
                                                  : isBuffer
                                                    ? "bg-muted-foreground/15 border-dashed border-muted-foreground/30 text-muted-foreground/60 cursor-not-allowed"
                                                    : "border-border hover:border-primary/50 hover:bg-primary/5"
                                            }`}
                                            onClick={() => handleSlotClick(slot)}
                                            data-testid={`slot-${slot.start_time}`}
                                            disabled={false} // Keep enabled so click handler can show toast
                                          >
                                            <Clock className="w-3 h-3 inline-block mr-1" />
                                            {slot.start_time}
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {isUnavailable ? (
                                            <span className="text-muted-foreground">
                                              {isBooked ? "Booked" : "Preparation buffer"}
                                            </span>
                                          ) : (
                                            <span>Available - Click to select</span>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                })}
                              </div>
                              
                              {/* Next Available Suggestion */}
                              {nextAvailableSuggestion && (
                                <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                                  {nextAvailableSuggestion.noAvailability ? (
                                    <>
                                      <p className="text-sm font-medium text-amber-600">No availability remaining today</p>
                                      <Button 
                                        variant="link" 
                                        className="p-0 h-auto text-amber-600 underline"
                                        onClick={() => {
                                          setFormData(prev => ({ ...prev, event_date: null }));
                                          setNextAvailableSuggestion(null);
                                        }}
                                      >
                                        Choose another date
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-sm font-medium text-amber-600">
                                        Next available: {nextAvailableSuggestion.time}
                                      </p>
                                      <Button 
                                        variant="link" 
                                        className="p-0 h-auto text-amber-600 underline"
                                        onClick={() => {
                                          const slot = {
                                            ...nextAvailableSuggestion.slot,
                                            date: nextAvailableSuggestion.date,
                                            available: true
                                          };
                                          handleSlotToggle(slot);
                                          setNextAvailableSuggestion(null);
                                        }}
                                      >
                                        Select this time
                                      </Button>
                                    </>
                                  )}
                                </div>
                              )}
                              
                              {/* Selected Slots Summary */}
                              {selectedSlots.length > 0 && (
                                <div className="mt-4 p-4 bg-primary/10 border border-primary/30 rounded-sm">
                                  <p className="text-sm font-medium mb-1">Selected Time</p>
                                  <p className="text-lg">
                                    {selectedSlots[0].start_time} - {selectedSlots[selectedSlots.length - 1].end_time}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {selectedSlots.length * 30} minutes total
                                  </p>
                                  {selectedSlots[0].location && (
                                    <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {selectedSlots[0].location}
                                    </p>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="p-6 bg-muted/30 rounded-sm text-center">
                              <p className="text-muted-foreground">No available slots for this date</p>
                              <Button 
                                variant="link" 
                                className="mt-2"
                                onClick={() => setBookingMode("request")}
                              >
                                Request a different time instead
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Custom Request Mode */
                    <div className="space-y-6">
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                          <p className="text-sm text-blue-200">
                            You&apos;re requesting a custom time. The photographer will review your request and confirm availability.
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="mb-2 block">Preferred Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left" data-testid="event-date">
                              {formData.event_date
                                ? format(formData.event_date, "PPP")
                                : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={formData.event_date}
                              onSelect={(date) => setFormData(prev => ({ ...prev, event_date: date }))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label className="mb-2 block">Start Time</Label>
                          <Input
                            type="time"
                            value={formData.start_time}
                            onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                            className="form-input"
                            data-testid="start-time"
                          />
                        </div>
                        <div>
                          <Label className="mb-2 block">End Time</Label>
                          <Input
                            type="time"
                            value={formData.end_time}
                            onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                            className="form-input"
                            data-testid="end-time"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Venue Info */}
              <div className="space-y-4 pt-4 border-t border-border">
                <div>
                  <Label className="mb-2 block">Venue Name</Label>
                  <Input
                    value={formData.venue_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, venue_name: e.target.value }))}
                    placeholder="e.g., City Sports Complex"
                    className="form-input"
                    data-testid="venue-name"
                  />
                </div>

                <div>
                  <Label className="mb-2 block">Venue Address</Label>
                  <Input
                    value={formData.venue_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, venue_address: e.target.value }))}
                    placeholder="Full address"
                    className="form-input"
                    data-testid="venue-address"
                  />
                </div>
              </div>

              {/* Sports-specific fields */}
              {formData.event_type === "Sports" && (
                <div className="space-y-4 pt-4 border-t border-border">
                  <div>
                    <Label className="mb-2 block">Team Name (optional)</Label>
                    <Input
                      value={formData.team_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, team_name: e.target.value }))}
                      placeholder="e.g., Eagles"
                      className="form-input"
                      data-testid="team-name"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Jersey Numbers (optional)</Label>
                    <Input
                      value={formData.jersey_numbers}
                      onChange={(e) => setFormData(prev => ({ ...prev, jersey_numbers: e.target.value }))}
                      placeholder="e.g., 7, 10, 23"
                      className="form-input"
                      data-testid="jersey-numbers"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label className="mb-2 block">Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any special requests or details..."
                  className="form-textarea"
                  rows={4}
                  data-testid="notes"
                />
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(1)}
                  data-testid="step2-back"
                >
                  Back
                </Button>
                <Button
                  className="btn-primary rounded-sm flex-1"
                  disabled={!canProceedStep2}
                  onClick={() => setStep(3)}
                  data-testid="step2-next"
                >
                  Continue <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Contact Details */}
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl mb-2">Your Contact Information</h2>
                <p className="text-muted-foreground text-sm">We&apos;ll use this to confirm your booking</p>
              </div>

              <div>
                <Label className="mb-2 block">Full Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="John Doe"
                  className="form-input"
                  data-testid="customer-name"
                />
              </div>

              <div>
                <Label className="mb-2 block">Email *</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="john@example.com"
                  className="form-input"
                  data-testid="customer-email"
                />
              </div>

              <div>
                <Label className="mb-2 block">Phone *</Label>
                <Input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="form-input"
                  data-testid="customer-phone"
                />
              </div>

              <div>
                <Label className="mb-2 block">Instagram Handle (optional)</Label>
                <Input
                  value={formData.customer_instagram}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_instagram: e.target.value }))}
                  placeholder="@yourhandle"
                  className="form-input"
                  data-testid="customer-instagram"
                />
              </div>

              <div className="bg-muted/50 p-4 rounded-sm">
                <h4 className="font-medium mb-2">Booking Summary</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Event: {formData.event_type}</p>
                  <p>Services: {formData.request_types.join(", ")}</p>
                  <p>
                    Date: {formData.is_tournament 
                      ? `${formData.tournament_start_date ? format(formData.tournament_start_date, "PP") : ""} - ${formData.tournament_end_date ? format(formData.tournament_end_date, "PP") : ""}`
                      : formData.event_date ? format(formData.event_date, "PP") : "Not set"
                    }
                  </p>
                  {formData.start_time && formData.end_time && (
                    <p>Time: {formData.start_time} - {formData.end_time}</p>
                  )}
                  {formData.venue_name && <p>Venue: {formData.venue_name}</p>}
                  {selectedSlots.length > 0 && (
                    <Badge variant="outline" className="mt-2 text-primary border-primary">
                      Booking from available slot
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(2)}
                  data-testid="step3-back"
                >
                  Back
                </Button>
                <Button
                  className="btn-primary rounded-sm flex-1"
                  disabled={!canSubmit || loading}
                  onClick={handleSubmit}
                  data-testid="submit-booking"
                >
                  {loading ? (
                    <span className="spinner" />
                  ) : (
                    <>Submit Request</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default BookingPage;
